import CompositeError from './CompositeError';
import logger from '../../log';
const log = logger.getLogger(__filename);

/**
 * Returns a function that wraps the function `f` and retries it `tries` times.
 *
 * @param f - function to retry. It should return a promise
 * @param tries - number of times to try the function before failing.
 * @param shouldRetry - function that checks the error and returns true if retry should continue.
 * @param compositeError - Container for composite error handling.
 */
function wrapRetry<Args extends Array<unknown>, Retry, Result>(
  f: (...args: Args) => Promise<Result>,
  tries: number,
  shouldRetry: (x: Retry) => boolean = () => true,
  compositeError = new CompositeError('wrapRetry')
) {
  return async (...args: Args): Promise<Result> => {
    return f(...args).catch(err => {
      compositeError.addError(err);
      // it's been called at least once already, so the base case is 1
      if (tries > 1 && shouldRetry(err)) {
        log.debug('adding new retry for', args);
        return wrapRetry(f, tries - 1, shouldRetry, compositeError)(...args);
      } else {
        throw compositeError;
      }
    });
  };
}

export default wrapRetry;
